import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Upload, Loader2, ExternalLink, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/domains/auth/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { storageService } from "@/services/supabase/storage";
import { Database } from "@/integrations/supabase/types";

type ReportVersion = Database["public"]["Tables"]["report_versions"]["Row"];
type UploaderProfile = Pick<
  Database["public"]["Tables"]["profiles"]["Row"],
  "id" | "display_name" | "email" | "username" | "username_slug"
>;
type ReportVersionWithUploader = ReportVersion & { uploader?: UploaderProfile | null };

const REPORT_NAME = "state_of_design_europe_2025";
const STORAGE_BUCKET = "reports";

const formatDate = (dateString: string | null): string => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatBytes = (bytes: number): string => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, index)).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const parseVersionLabel = (label?: string | null) => {
  if (!label) return null;
  const match = label.match(/^v(\d+)\.(\d+)$/i);
  if (!match) return null;
  return { major: Number(match[1]), minor: Number(match[2]) };
};

const getNextVersionLabel = (latestLabel?: string | null) => {
  if (!latestLabel) return "v1.0";
  const parsed = parseVersionLabel(latestLabel);
  if (!parsed) return "v1.0";
  return `v${parsed.major}.${parsed.minor + 1}`;
};

const getUploaderLabel = (uploader?: UploaderProfile | null) => {
  if (!uploader) return "-";
  return uploader.display_name || uploader.username || uploader.email || "-";
};

export default function ReportVersionsManager() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [versionNotes, setVersionNotes] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const {
    data: versions,
    isLoading,
    error,
    refetch,
  } = useQuery<ReportVersionWithUploader[]>({
    queryKey: ["report-versions", REPORT_NAME],
    queryFn: async () => {
      const { data, error: queryError } = await supabase
        .from("report_versions")
        .select("*")
        .eq("report_name", REPORT_NAME)
        .order("created_at", { ascending: false });

      if (queryError) {
        throw new Error(queryError.message);
      }

      const versionRows = (data as ReportVersion[]) || [];
      const uploaderIds = Array.from(
        new Set(versionRows.map((version) => version.uploaded_by).filter(Boolean))
      ) as string[];

      if (uploaderIds.length === 0) {
        return versionRows;
      }

      const { data: uploaderProfiles, error: uploaderError } = await supabase
        .from("profiles")
        .select("id, display_name, email, username, username_slug")
        .in("id", uploaderIds);

      if (uploaderError) {
        console.error("Error fetching report uploaders:", uploaderError);
        return versionRows;
      }

      const uploaderMap = new Map(
        (uploaderProfiles || []).map((profile) => [profile.id, profile])
      );

      return versionRows.map((version) => ({
        ...version,
        uploader: version.uploaded_by ? uploaderMap.get(version.uploaded_by) ?? null : null,
      }));
    },
  });

  const activeVersion = useMemo(
    () => versions?.find((version) => version.status === "active") || null,
    [versions]
  );

  const nextVersionLabel = useMemo(
    () => getNextVersionLabel(activeVersion?.version_label || versions?.[0]?.version_label),
    [activeVersion?.version_label, versions]
  );

  const handleFileSelection = (file: File | null) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast({
        title: "Invalid file type",
        description: "Please upload a PDF file.",
        variant: "destructive",
      });
      return;
    }
    setSelectedFile(file);
  };

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0] || null;
    handleFileSelection(file);
  };

  const handlePreview = async (version: ReportVersionWithUploader) => {
    try {
      const { data, error: previewError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .createSignedUrl(version.storage_path, 300);

      if (previewError || !data?.signedUrl) {
        throw previewError || new Error("Failed to generate preview link");
      }

      window.open(data.signedUrl, "_blank", "noopener,noreferrer");
    } catch (previewError) {
      toast({
        title: "Preview failed",
        description:
          previewError instanceof Error
            ? previewError.message
            : "Unable to open the report preview.",
        variant: "destructive",
      });
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast({
        title: "No file selected",
        description: "Choose a PDF before uploading.",
        variant: "destructive",
      });
      return;
    }

    if (!user?.id) {
      toast({
        title: "Authentication required",
        description: "Please sign in again before uploading.",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);

    try {
      const fileExt = selectedFile.name.split(".").pop() || "pdf";
      const baseName = selectedFile.name.replace(/\.[^/.]+$/, "");
      const safeBaseName = baseName
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9-_]+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-+|-+$/g, "");
      const storageFileName = `${safeBaseName || "report"}-${nextVersionLabel}.${fileExt}`;
      const folder = `${REPORT_NAME}/${nextVersionLabel}`;

      const uploadResult = await storageService.uploadFile(selectedFile, {
        bucket: STORAGE_BUCKET,
        folder,
        fileName: storageFileName,
        maxSizeBytes: 100 * 1024 * 1024,
        allowedTypes: ["application/pdf"],
        cacheControl: "3600",
        skipCompression: true,
      });

      const { data: newVersion, error: insertError } = await supabase
        .from("report_versions")
        .insert({
          report_name: REPORT_NAME,
          version_label: nextVersionLabel,
          file_name: storageFileName,
          storage_path: uploadResult.path,
          file_size_bytes: uploadResult.size,
          content_type: selectedFile.type || "application/pdf",
          status: "active",
          version_notes: versionNotes.trim() || null,
          published_at: new Date().toISOString(),
          uploaded_by: user.id,
        })
        .select("*")
        .single();

      if (insertError || !newVersion) {
        throw insertError || new Error("Failed to save report version.");
      }

      const { error: archiveError } = await supabase
        .from("report_versions")
        .update({ status: "archived" })
        .eq("report_name", REPORT_NAME)
        .eq("status", "active")
        .neq("id", newVersion.id);

      if (archiveError) {
        console.error("Failed to archive previous report versions:", archiveError);
      }

      toast({
        title: "Report uploaded",
        description: `Version ${nextVersionLabel} is now active. It can take a minute for download links to update.`,
      });

      setSelectedFile(null);
      setVersionNotes("");
      await refetch();
    } catch (uploadError) {
      console.error("Report upload error:", uploadError);
      toast({
        title: "Upload failed",
        description:
          uploadError instanceof Error
            ? uploadError.message
            : "Unable to upload this report version.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-[1fr_2fr]">
        <Card>
          <CardHeader>
            <CardTitle>Current Version</CardTitle>
            <CardDescription>Active report served to users.</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading report versions...
              </div>
            ) : activeVersion ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-800 border-green-200">
                    Active
                  </Badge>
                  <span className="text-sm font-medium">{activeVersion.version_label}</span>
                </div>
                <div className="text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span>{activeVersion.file_name}</span>
                  </div>
                  <div className="mt-1">Uploaded {formatDate(activeVersion.created_at)}</div>
                  <div className="mt-1">Uploaded by {getUploaderLabel(activeVersion.uploader)}</div>
                  <div className="mt-1">Size: {formatBytes(activeVersion.file_size_bytes)}</div>
                  {activeVersion.version_notes && (
                    <div className="mt-2 text-xs text-gray-600">
                      Notes: {activeVersion.version_notes}
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePreview(activeVersion)}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Preview
                </Button>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                No active report version yet.
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Upload New Version</CardTitle>
            <CardDescription>
              Next version will be <strong>{nextVersionLabel}</strong>.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <label
              className={`flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-6 text-center transition ${
                isDragging ? "border-primary bg-primary/5" : "border-gray-200"
              }`}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(event) => handleFileSelection(event.target.files?.[0] || null)}
              />
              <Upload className="h-6 w-6 text-muted-foreground" />
              <div className="text-sm font-medium">
                Drag a PDF here, or click to browse
              </div>
              <div className="text-xs text-muted-foreground">
                PDF only. Max 100MB.
              </div>
              {selectedFile && (
                <div className="text-xs text-gray-600">
                  Selected: {selectedFile.name}
                </div>
              )}
            </label>

            <Textarea
              placeholder="Version notes (optional)"
              value={versionNotes}
              onChange={(event) => setVersionNotes(event.target.value)}
              rows={3}
            />

            <Button onClick={handleUpload} disabled={isUploading || !selectedFile}>
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                "Publish Version"
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Version History</CardTitle>
          <CardDescription>
            Previous versions remain available for audit and rollback.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-red-600">Error loading versions: {error.message}</div>
          ) : !versions || versions.length === 0 ? (
            <div className="text-muted-foreground">No report versions found.</div>
          ) : (
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Version</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead>Uploaded by</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-right">Preview</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {versions.map((version) => (
                    <TableRow key={version.id}>
                      <TableCell className="font-medium">{version.version_label}</TableCell>
                      <TableCell>
                        <Badge
                          className={
                            version.status === "active"
                              ? "bg-green-100 text-green-800 border-green-200"
                              : "bg-gray-100 text-gray-600 border-gray-200"
                          }
                        >
                          {version.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(version.created_at)}</TableCell>
                      <TableCell>
                        <div className="text-sm font-medium">{getUploaderLabel(version.uploader)}</div>
                        {version.uploader?.email && version.uploader.email !== getUploaderLabel(version.uploader) && (
                          <div className="text-xs text-muted-foreground">{version.uploader.email}</div>
                        )}
                      </TableCell>
                      <TableCell>{formatBytes(version.file_size_bytes)}</TableCell>
                      <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                        {version.version_notes || "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePreview(version)}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
